import { Fragment, useEffect, useMemo, useState } from 'react';
import { AttendanceApi, HotLeadApi, type AttendanceRow, type WhuChannel } from '../lib/crmApi';
import { formatDateTime, formatPhone } from '../lib/format';
import { useToast } from '../components/Toast';

const CHANNELS: { id: WhuChannel; label: string }[] = [
  { id: 'default', label: 'WhatsApp principal' },
  { id: 'fallback', label: 'WhatsApp fallback' },
  { id: 'fb_ig', label: 'Facebook / Instagram' },
  { id: 'meta_api', label: 'Canal Meta API' },
];

const SOURCE_EVENTS = [
  { id: 'menu_agendar', label: 'Menu: Agendar' },
  { id: 'menu_consulta', label: 'Menu: Consulta' },
  { id: 'menu_exames', label: 'Menu: Exames' },
  { id: 'menu_pacote', label: 'Menu: Pacote saúde' },
  { id: 'menu_emagrecimento', label: 'Menu: Emagrecimento' },
  { id: 'comprar', label: 'Intenção de comprar' },
  { id: 'sangue', label: 'Coleta de sangue' },
];

interface TransferState {
  open: boolean;
  attendance: AttendanceRow | null;
  sourceEvent: string;
  channel: WhuChannel;
  loading: boolean;
  result: string | null;
}

const initialTransfer: TransferState = {
  open: false,
  attendance: null,
  sourceEvent: 'menu_agendar',
  channel: 'default',
  loading: false,
  result: null,
};

export default function Atendimentos() {
  const toast = useToast();
  const [channel, setChannel] = useState<WhuChannel>('default');
  const [statusFilter, setStatusFilter] = useState<string>('open');
  const [searchTerm, setSearchTerm] = useState('');
  const [rows, setRows] = useState<AttendanceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [noteModal, setNoteModal] = useState<{ row: AttendanceRow; text: string } | null>(null);
  const [transfer, setTransfer] = useState<TransferState>(initialTransfer);

  async function reload() {
    setLoading(true);
    const r = await AttendanceApi.list({ channel, status: statusFilter || undefined, limit: 200 });
    if (!r.ok) {
      toast.error(`Falha ao carregar atendimentos: ${r.error ?? 'erro'}`);
      setRows([]);
    } else {
      const payload = r.data?.data;
      const list = Array.isArray(payload)
        ? payload
        : (payload?.data ?? payload?.chats ?? payload?.items ?? []);
      setRows(list as AttendanceRow[]);
    }
    setLoading(false);
  }

  useEffect(() => {
    reload();
    // auto-refresh 30s pra aproximar do tempo real (Opção A sem webhook)
    const t = setInterval(reload, 30_000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channel, statusFilter]);

  const filtered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      return (
        (r.contactName ?? '').toLowerCase().includes(q) ||
        (r.contactPhone ?? '').toLowerCase().includes(q) ||
        (r.lastMessage ?? '').toLowerCase().includes(q)
      );
    });
  }, [rows, searchTerm]);

  const rowId = (r: AttendanceRow, idx: number) => String(r.id ?? r.uuid ?? idx);

  function openTransfer(att: AttendanceRow) {
    setTransfer({ ...initialTransfer, open: true, attendance: att, channel });
  }

  async function confirmTransfer() {
    const att = transfer.attendance;
    if (!att) return;
    const id = String(att.id ?? att.uuid ?? '');
    if (!id) {
      setTransfer((t) => ({ ...t, result: 'Atendimento sem ID válido' }));
      return;
    }
    setTransfer((t) => ({ ...t, loading: true, result: null }));
    const r = await HotLeadApi.transfer({
      attendance_id: id,
      source_event: transfer.sourceEvent,
      channel: transfer.channel,
    });
    setTransfer((t) => ({
      ...t,
      loading: false,
      result: r.ok
        ? r.data?.transferred
          ? `✓ Transferido para ${r.data?.target ?? 'atendente'} (decisão: ${r.data?.decision ?? '—'})`
          : `Sem atendente disponível. Caiu na roleta WHU (fallback).`
        : `Erro: ${r.error}`,
    }));
    if (r.ok) {
      if (r.data?.transferred) toast.success(`Transferido para ${r.data?.target ?? 'atendente'}`);
      else toast.warn('Ninguém online — fallback roleta WHU');
      setTimeout(reload, 1500);
    } else {
      toast.error(`Falha ao transferir: ${r.error ?? 'erro'}`);
    }
  }

  async function finalizeChat(row: AttendanceRow, idx: number) {
    const id = rowId(row, idx);
    if (!confirm(`Finalizar atendimento com ${row.contactName ?? 'este contato'}?`)) return;
    setBusyId(id);
    const r = await AttendanceApi.finalize(id, channel);
    setBusyId(null);
    if (!r.ok) {
      toast.error(`Falha ao finalizar: ${r.error ?? 'erro'}`);
      return;
    }
    toast.success('Atendimento finalizado');
    setTimeout(reload, 800);
  }

  async function sendNote() {
    if (!noteModal) return;
    const { row, text } = noteModal;
    if (!text.trim()) { toast.warn('Escreva a nota'); return; }
    const number = row.contactPhone;
    if (!number) { toast.error('Contato sem telefone'); return; }

    setNoteModal({ ...noteModal, text });
    const r = await AttendanceApi.note({
      id: String(row.id ?? row.uuid ?? ''),
      channel,
      number,
      message: text,
      contactId: typeof row.contactId === 'string' ? row.contactId : undefined,
    });
    if (!r.ok) {
      toast.error(`Falha ao enviar nota: ${r.error ?? 'erro'}`);
      return;
    }
    toast.success('Nota interna enviada (apenas atendentes veem)');
    setNoteModal(null);
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold">Atendimentos</h1>
          <p className="text-xs text-slate-500 mt-0.5">
            Atualiza sozinho a cada 30s · {filtered.length} chat{filtered.length === 1 ? '' : 's'} exibido{filtered.length === 1 ? '' : 's'}
            {searchTerm && <> · buscando "{searchTerm}"</>}
          </p>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-4 mb-4 grid grid-cols-1 md:grid-cols-4 gap-3">
        <select
          value={channel}
          onChange={(e) => setChannel(e.target.value as WhuChannel)}
          className="px-3 py-2 border border-slate-300 rounded-md text-sm"
        >
          {CHANNELS.map((c) => (
            <option key={c.id} value={c.id}>{c.label}</option>
          ))}
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 border border-slate-300 rounded-md text-sm"
        >
          <option value="open">Abertos</option>
          <option value="">Todos</option>
          <option value="pending">Pendentes</option>
          <option value="closed">Fechados</option>
        </select>
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Buscar por nome, telefone, mensagem…"
          className="px-3 py-2 border border-slate-300 rounded-md text-sm"
        />
        <button
          onClick={reload}
          className="px-3 py-2 border border-slate-300 rounded-md text-sm bg-slate-50 hover:bg-slate-100"
        >
          Atualizar agora
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-left text-xs uppercase tracking-wide">
            <tr>
              <th className="px-4 py-3 w-4"></th>
              <th className="px-4 py-3">Contato</th>
              <th className="px-4 py-3">Telefone</th>
              <th className="px-4 py-3">Última mensagem</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 text-right">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">Carregando…</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">
                {rows.length === 0 ? 'Nenhum atendimento encontrado.' : 'Nenhum resultado pra essa busca.'}
              </td></tr>
            )}
            {filtered.map((row, idx) => {
              const id = rowId(row, idx);
              const last = row.lastMessage ?? (row.lastMessageAt ? formatDateTime(row.lastMessageAt) : '—');
              const expanded = selectedId === id;
              const unread = typeof row.unreadCount === 'number' && row.unreadCount > 0;
              return (
                <Fragment key={id}>
                  <tr
                    className={`hover:bg-slate-50 cursor-pointer ${expanded ? 'bg-blue-50/40' : ''}`}
                    onClick={() => setSelectedId(expanded ? null : id)}
                  >
                    <td className="px-4 py-3 text-slate-400 text-xs select-none">{expanded ? '▾' : '▸'}</td>
                    <td className="px-4 py-3 font-medium text-slate-800">
                      {row.contactName ?? <span className="text-slate-400">(sem nome)</span>}
                      {unread && (
                        <span className="ml-2 text-[10px] bg-rose-500 text-white px-1.5 py-0.5 rounded-full">
                          {row.unreadCount}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-600 font-mono text-xs">
                      {formatPhone(row.contactPhone ?? null)}
                    </td>
                    <td className="px-4 py-3 text-slate-500 max-w-md truncate">{last}</td>
                    <td className="px-4 py-3">
                      <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">
                        {row.status ?? '—'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={(e) => { e.stopPropagation(); openTransfer(row); }}
                        disabled={busyId === id}
                        className="text-xs px-3 py-1.5 rounded-md bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-50"
                      >
                        Hot lead
                      </button>
                    </td>
                  </tr>
                  {expanded && (
                    <tr className="bg-blue-50/20">
                      <td></td>
                      <td colSpan={5} className="px-4 py-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <h4 className="text-xs uppercase tracking-wide text-slate-500 mb-2">Detalhes</h4>
                            <dl className="text-sm space-y-1">
                              <Field label="ID" value={id} mono />
                              <Field label="Atribuído a" value={String(row.assignedTo ?? '—')} />
                              <Field label="Setor WHU" value={String(row.sectorId ?? '—')} />
                              <Field
                                label="Última mensagem em"
                                value={row.lastMessageAt ? formatDateTime(row.lastMessageAt) : '—'}
                              />
                              <Field label="Não lidas" value={String(row.unreadCount ?? 0)} />
                            </dl>
                          </div>
                          <div>
                            <h4 className="text-xs uppercase tracking-wide text-slate-500 mb-2">Ações</h4>
                            <div className="flex flex-wrap gap-2">
                              <button
                                onClick={() => setNoteModal({ row, text: '' })}
                                className="text-xs px-3 py-1.5 rounded-md border border-slate-300 bg-white hover:bg-slate-50"
                              >
                                ✎ Nota interna
                              </button>
                              <button
                                onClick={() => finalizeChat(row, idx)}
                                disabled={busyId === id}
                                className="text-xs px-3 py-1.5 rounded-md border border-slate-300 bg-white hover:bg-slate-50 disabled:opacity-50"
                              >
                                {busyId === id ? 'Finalizando…' : '✓ Finalizar'}
                              </button>
                              <button
                                onClick={() => openTransfer(row)}
                                className="text-xs px-3 py-1.5 rounded-md bg-rose-600 text-white hover:bg-rose-700"
                              >
                                → Transferir hot lead
                              </button>
                            </div>
                            <p className="text-xs text-slate-500 mt-3 leading-snug">
                              <strong>Nota interna</strong> é visível apenas pros atendentes (isWhisper no WHU), não vai pro paciente.
                              <br />
                              <strong>Finalizar</strong> encerra o chat no WHU.
                            </p>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {transfer.open && (
        <div className="fixed inset-0 bg-slate-900/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h2 className="text-lg font-semibold mb-1">Transferir hot lead</h2>
            <p className="text-sm text-slate-500 mb-4">
              {transfer.attendance?.contactName ?? 'Contato'} · {formatPhone(transfer.attendance?.contactPhone ?? null)}
            </p>
            <label className="block text-xs uppercase tracking-wide text-slate-500 mb-1">Motivo / origem</label>
            <select
              value={transfer.sourceEvent}
              onChange={(e) => setTransfer((t) => ({ ...t, sourceEvent: e.target.value, result: null }))}
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm mb-3"
            >
              {SOURCE_EVENTS.map((s) => (
                <option key={s.id} value={s.id}>{s.label}</option>
              ))}
            </select>
            <label className="block text-xs uppercase tracking-wide text-slate-500 mb-1">Canal WHU</label>
            <select
              value={transfer.channel}
              onChange={(e) => setTransfer((t) => ({ ...t, channel: e.target.value as WhuChannel, result: null }))}
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm mb-4"
            >
              {CHANNELS.map((c) => (
                <option key={c.id} value={c.id}>{c.label}</option>
              ))}
            </select>

            {transfer.result && (
              <div className={`text-sm rounded-md p-3 mb-4 ${
                transfer.result.startsWith('✓')
                  ? 'bg-emerald-50 border border-emerald-200 text-emerald-800'
                  : transfer.result.startsWith('Erro')
                    ? 'bg-red-50 border border-red-200 text-red-700'
                    : 'bg-amber-50 border border-amber-200 text-amber-800'
              }`}>
                {transfer.result}
              </div>
            )}

            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setTransfer(initialTransfer)}
                className="px-4 py-2 text-sm rounded-md border border-slate-300 hover:bg-slate-50"
              >Fechar</button>
              <button
                onClick={confirmTransfer}
                disabled={transfer.loading}
                className="px-4 py-2 text-sm rounded-md bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-40"
              >{transfer.loading ? 'Transferindo…' : 'Confirmar'}</button>
            </div>
          </div>
        </div>
      )}

      {noteModal && (
        <div
          className="fixed inset-0 bg-slate-900/40 flex items-center justify-center z-50 p-4"
          onClick={() => setNoteModal(null)}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-lg font-semibold mb-1">Nota interna</h2>
            <p className="text-sm text-slate-500 mb-4">
              {noteModal.row.contactName ?? 'Contato'} · {formatPhone(noteModal.row.contactPhone ?? null)}
              <br />
              <span className="text-xs text-amber-700">
                Apenas atendentes leem. O paciente NÃO recebe essa mensagem.
              </span>
            </p>
            <textarea
              value={noteModal.text}
              onChange={(e) => setNoteModal({ ...noteModal, text: e.target.value })}
              placeholder="Ex: cliente interessada em pacote saúde, agendar retorno segunda…"
              rows={4}
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm mb-4 resize-none"
            />
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setNoteModal(null)}
                className="px-4 py-2 text-sm rounded-md border border-slate-300 hover:bg-slate-50"
              >Cancelar</button>
              <button
                onClick={sendNote}
                className="px-4 py-2 text-sm rounded-md bg-slate-700 text-white hover:bg-slate-800"
              >Enviar nota</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex gap-2">
      <dt className="text-slate-500 text-xs w-24 pt-0.5">{label}</dt>
      <dd className={`text-slate-800 ${mono ? 'font-mono text-xs' : ''}`}>{value}</dd>
    </div>
  );
}
